import { useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Menu,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconPlus,
  IconTrash,
  IconPencil,
  IconX,
  IconCheck,
  IconRefresh,
  IconTruckDelivery,
  IconHistory,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { SafeDateInput } from "@/components/SafeDateInput";
import type {
  Order,
  OrderDocument,
  DocDirection,
  DocKind,
  DocumentsListResponse,
  DocumentCreateRequest,
  TrackerEventsResponse,
} from "@/types/api";

// DocumentsTab — раздел «Документы» карточки заявки.
// Требование ТЗ 01.07.2026 п.2: «раздел входящие документы + Почта России».
// См. project_debtor_report_structure.md и скрин Trans-Manager вкладки ТТН.

interface Props {
  order: Order;
  isLocked: boolean;
}

const KIND_LABEL: Record<DocKind, string> = {
  invoice: "Счёт",
  invoice_factura: "Счёт-фактура",
  act: "Акт",
  ttn: "ТТН",
  other: "Прочее",
};

const KIND_OPTIONS: { value: DocKind; label: string }[] = [
  { value: "invoice", label: "Счёт" },
  { value: "invoice_factura", label: "Счёт-фактура" },
  { value: "act", label: "Акт" },
  { value: "ttn", label: "ТТН" },
  { value: "other", label: "Прочее" },
];

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  const d = iso.slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
}

function toDateISO(d: Date | null | undefined): string | undefined {
  if (!d) return undefined;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseDate(iso?: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function DocumentsTab({ order, isLocked }: Props) {
  const qc = useQueryClient();
  const orderID = order.id;

  const docsQ = useQuery({
    queryKey: ["orders", orderID, "documents"],
    queryFn: async () =>
      (await api.get<DocumentsListResponse>(`/api/v1/orders/${orderID}/documents`)).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["orders", orderID, "documents"] });
  };

  const createMut = useMutation({
    mutationFn: async (body: DocumentCreateRequest) => {
      const r = await api.post<OrderDocument>(`/api/v1/orders/${orderID}/documents`, body);
      return r.data;
    },
    onSuccess: () => {
      invalidate();
      notifications.show({ message: "Документ добавлен", color: "teal" });
    },
    onError: () => notifications.show({ message: "Не удалось добавить документ", color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: DocumentCreateRequest }) => {
      const r = await api.patch<OrderDocument>(`/api/v1/orders/${orderID}/documents/${id}`, body);
      return r.data;
    },
    onSuccess: () => {
      invalidate();
      notifications.show({ message: "Документ обновлён", color: "teal" });
    },
    onError: () => notifications.show({ message: "Не удалось обновить документ", color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/orders/${orderID}/documents/${id}`);
    },
    onSuccess: () => {
      invalidate();
      notifications.show({ message: "Документ удалён", color: "teal" });
    },
    onError: () => notifications.show({ message: "Не удалось удалить документ", color: "red" }),
  });

  const items = docsQ.data?.items ?? [];
  const incoming = items.filter((d) => d.direction === "incoming");
  const outgoing = items.filter((d) => d.direction === "outgoing");

  return (
    <Stack gap="lg">
      <Alert variant="light" color="yellow" title="Почта России: интеграция ещё не подключена">
        Кнопка «Обновить» у трек-кода заработает после получения API-ключа.
        Пока статусы отправлений отслеживайте на pochta.ru и вносите даты вручную.
      </Alert>

      <DocSection
        title="Входящие от перевозчика"
        subtitle={order.carrier_name ?? "(перевозчик не выбран)"}
        direction="incoming"
        documents={incoming}
        isLocked={isLocked}
        onCreate={(body) => createMut.mutate(body)}
        onUpdate={(id, body) => updateMut.mutate({ id, body })}
        onDelete={(id) => deleteMut.mutate(id)}
        creating={createMut.isPending}
      />

      <DocSection
        title="Исходящие заказчику"
        subtitle={order.client_name ?? "(заказчик не выбран)"}
        direction="outgoing"
        documents={outgoing}
        isLocked={isLocked}
        onCreate={(body) => createMut.mutate(body)}
        onUpdate={(id, body) => updateMut.mutate({ id, body })}
        onDelete={(id) => deleteMut.mutate(id)}
        creating={createMut.isPending}
      />
    </Stack>
  );
}

// ─── Секция «Входящие» / «Исходящие» ────────────────────────────────────

interface SectionProps {
  title: string;
  subtitle: string;
  direction: DocDirection;
  documents: OrderDocument[];
  isLocked: boolean;
  onCreate: (body: DocumentCreateRequest) => void;
  onUpdate: (id: string, body: DocumentCreateRequest) => void;
  onDelete: (id: string) => void;
  creating: boolean;
}

function DocSection({
  title,
  subtitle,
  direction,
  documents,
  isLocked,
  onCreate,
  onUpdate,
  onDelete,
  creating,
}: SectionProps) {
  const [adding, setAdding] = useState<DocKind | null>(null);
  const [editingID, setEditingID] = useState<string | null>(null);

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="sm">
        <div>
          <Title order={5}>{title}</Title>
          <Text size="xs" c="dimmed">{subtitle}</Text>
        </div>
        {!isLocked && (
          <Menu position="bottom-end">
            <Menu.Target>
              <Button size="xs" leftSection={<IconPlus size={14} />} variant="light">
                Добавить
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {KIND_OPTIONS.map((k) => (
                <Menu.Item
                  key={k.value}
                  onClick={() => { setAdding(k.value); setEditingID(null); }}
                >
                  {k.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {adding && (
        <DocumentForm
          direction={direction}
          initialKind={adding}
          submitting={creating}
          onSubmit={(body) => { onCreate(body); setAdding(null); }}
          onCancel={() => setAdding(null)}
        />
      )}

      {documents.length === 0 && !adding && (
        <Text c="dimmed" size="sm">Документов нет</Text>
      )}

      {documents.length > 0 && (
        <Table striped highlightOnHover withTableBorder withColumnBorders mt="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 110 }}>Тип</Table.Th>
              <Table.Th style={{ width: 110 }}>№</Table.Th>
              <Table.Th style={{ width: 110 }}>Дата док.</Table.Th>
              {direction === "incoming" ? (
                <Table.Th style={{ width: 130 }}>Принят от перев.</Table.Th>
              ) : (
                <>
                  <Table.Th style={{ width: 130 }}>Отправлен</Table.Th>
                  <Table.Th style={{ width: 130 }}>Оригиналы получены</Table.Th>
                  <Table.Th>Почта России</Table.Th>
                </>
              )}
              <Table.Th style={{ width: 100 }} ta="right">Сумма</Table.Th>
              <Table.Th>Примечание</Table.Th>
              <Table.Th style={{ width: 80 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {documents.map((d) => editingID === d.id ? (
              <Table.Tr key={d.id}>
                <Table.Td colSpan={direction === "outgoing" ? 9 : 7} p={0}>
                  <DocumentForm
                    direction={direction}
                    initialKind={d.kind}
                    initial={d}
                    submitting={false}
                    onSubmit={(body) => { onUpdate(d.id, body); setEditingID(null); }}
                    onCancel={() => setEditingID(null)}
                  />
                </Table.Td>
              </Table.Tr>
            ) : (
              <Table.Tr key={d.id}>
                <Table.Td><Badge variant="light" radius="sm">{KIND_LABEL[d.kind]}</Badge></Table.Td>
                <Table.Td>{d.doc_number || "—"}</Table.Td>
                <Table.Td>{fmtDate(d.doc_date)}</Table.Td>
                {direction === "incoming" ? (
                  <Table.Td>
                    {d.received_at ? fmtDate(d.received_at) : <Text c="red" size="sm">нет</Text>}
                  </Table.Td>
                ) : (
                  <>
                    <Table.Td>{fmtDate(d.sent_at)}</Table.Td>
                    <Table.Td>
                      {d.received_originals_at ? fmtDate(d.received_originals_at) : <Text c="red" size="sm">нет</Text>}
                    </Table.Td>
                    <Table.Td>
                      <TrackerCell document={d} isLocked={isLocked} />
                    </Table.Td>
                  </>
                )}
                <Table.Td ta="right" ff="monospace">
                  {d.amount != null ? d.amount.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                </Table.Td>
                <Table.Td>{d.note || "—"}</Table.Td>
                <Table.Td>
                  {!isLocked && (
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Редактировать">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => { setEditingID(d.id); setAdding(null); }}
                        >
                          <IconPencil size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Удалить">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Удалить документ «${KIND_LABEL[d.kind]} №${d.doc_number || "?"}»?`)) {
                              onDelete(d.id);
                            }
                          }}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}

// ─── Ячейка Почты России в исходящих ────────────────────────────────────

function TrackerCell({ document, isLocked }: { document: OrderDocument; isLocked: boolean }) {
  const qc = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);
  const orderID = document.order_id;

  const refreshMut = useMutation({
    mutationFn: async () => {
      const r = await api.post<TrackerEventsResponse>(
        `/api/v1/orders/${orderID}/documents/${document.id}/refresh-tracker`,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", orderID, "documents"] });
      qc.invalidateQueries({ queryKey: ["orders", orderID, "tracker", document.id] });
      notifications.show({ message: "Статус обновлён", color: "teal" });
    },
    onError: (e: unknown) => {
      // Ожидаемая ошибка когда клиент не подключён.
      const err = e as { response?: { status?: number; data?: { message?: string } } };
      if (err.response?.status === 503) {
        notifications.show({
          title: "Почта России не подключена",
          message: err.response.data?.message ?? "Интеграция ждёт API-ключ. Заполните события вручную.",
          color: "yellow",
        });
        return;
      }
      notifications.show({ message: "Не удалось обновить трекинг", color: "red" });
    },
  });

  if (!document.tracker_code) {
    return <Text c="dimmed" size="sm">—</Text>;
  }

  return (
    <Stack gap={2}>
      <Group gap={4} wrap="nowrap">
        <Text ff="monospace" size="xs">{document.tracker_code}</Text>
        {!isLocked && (
          <Tooltip label="Обновить статус на Почте России">
            <ActionIcon
              variant="subtle"
              size="xs"
              onClick={() => refreshMut.mutate()}
              loading={refreshMut.isPending}
            >
              <IconRefresh size={12} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="История событий">
          <ActionIcon
            variant="subtle"
            size="xs"
            onClick={() => setShowHistory((v) => !v)}
          >
            <IconHistory size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>
      {document.tracker_state && (
        <Badge variant="light" radius="sm" size="xs" color={
          document.tracker_state.toLowerCase().includes("вруч") ? "green" : "blue"
        }>
          {document.tracker_state}
        </Badge>
      )}
      {showHistory && <TrackerHistory orderID={orderID} documentID={document.id} />}
    </Stack>
  );
}

function TrackerHistory({ orderID, documentID }: { orderID: string; documentID: string }) {
  const q = useQuery({
    queryKey: ["orders", orderID, "tracker", documentID],
    queryFn: async () =>
      (await api.get<TrackerEventsResponse>(
        `/api/v1/orders/${orderID}/documents/${documentID}/tracker-events`,
      )).data,
  });
  if (q.isLoading) return <Text size="xs" c="dimmed">Загрузка...</Text>;
  const events = q.data?.items ?? [];
  if (events.length === 0) return <Text size="xs" c="dimmed">Событий нет (нажмите «Обновить»)</Text>;
  return (
    <Stack gap={1} mt={4}>
      {events.map((e) => (
        <Text key={e.id} size="xs" ff="monospace">
          {new Date(e.event_at).toLocaleString("ru-RU")} — {e.location} — {e.event}
        </Text>
      ))}
    </Stack>
  );
}

// ─── Форма ввода документа ──────────────────────────────────────────────

interface FormProps {
  direction: DocDirection;
  initialKind: DocKind;
  initial?: OrderDocument;
  submitting: boolean;
  onSubmit: (body: DocumentCreateRequest) => void;
  onCancel: () => void;
}

function DocumentForm({
  direction,
  initialKind,
  initial,
  submitting,
  onSubmit,
  onCancel,
}: FormProps) {
  const form = useForm<{
    kind: DocKind;
    doc_number: string;
    doc_date: Date | null;
    received_at: Date | null;
    sent_at: Date | null;
    received_originals_at: Date | null;
    tracker_code: string;
    amount: number | "";
    note: string;
  }>({
    initialValues: {
      kind: initial?.kind ?? initialKind,
      doc_number: initial?.doc_number ?? "",
      doc_date: parseDate(initial?.doc_date),
      received_at: parseDate(initial?.received_at),
      sent_at: parseDate(initial?.sent_at),
      received_originals_at: parseDate(initial?.received_originals_at),
      tracker_code: initial?.tracker_code ?? "",
      amount: initial?.amount ?? "",
      note: initial?.note ?? "",
    },
  });

  function handleSubmit() {
    const v = form.values;
    const body: DocumentCreateRequest = {
      direction,
      kind: v.kind,
      doc_number: v.doc_number,
      doc_date: toDateISO(v.doc_date),
      note: v.note,
    };
    if (v.amount !== "") body.amount = Number(v.amount);
    if (direction === "incoming") {
      body.received_at = toDateISO(v.received_at);
    } else {
      body.sent_at = toDateISO(v.sent_at);
      body.received_originals_at = toDateISO(v.received_originals_at);
      body.tracker_code = v.tracker_code;
    }
    onSubmit(body);
  }

  return (
    <Paper p="sm" bg="var(--mantine-color-gray-0)">
      <Stack gap="xs">
        <Group grow>
          <Select
            label="Тип документа"
            data={KIND_OPTIONS}
            {...form.getInputProps("kind")}
          />
          <TextInput
            label="Номер"
            placeholder="№ документа"
            {...form.getInputProps("doc_number")}
          />
          <SafeDateInput
            label="Дата документа"
            value={form.values.doc_date}
            onChange={(d) => form.setFieldValue("doc_date", d)}
          />
          <NumberInput
            label="Сумма (опционально)"
            hideControls
            thousandSeparator=" "
            decimalScale={2}
            value={form.values.amount}
            onChange={(v) => form.setFieldValue("amount", v === "" ? "" : Number(v))}
          />
        </Group>
        {direction === "incoming" ? (
          <Group grow>
            <SafeDateInput
              label="Принят от перевозчика"
              value={form.values.received_at}
              onChange={(d) => form.setFieldValue("received_at", d)}
            />
          </Group>
        ) : (
          <Group grow>
            <SafeDateInput
              label="Отправлен заказчику"
              value={form.values.sent_at}
              onChange={(d) => form.setFieldValue("sent_at", d)}
            />
            <SafeDateInput
              label="Оригиналы получены"
              value={form.values.received_originals_at}
              onChange={(d) => form.setFieldValue("received_originals_at", d)}
            />
            <TextInput
              label="Трек-код Почты России"
              placeholder="21403119403381"
              leftSection={<IconTruckDelivery size={14} />}
              {...form.getInputProps("tracker_code")}
            />
          </Group>
        )}
        <Textarea
          label="Примечание"
          placeholder="..."
          autosize
          minRows={1}
          {...form.getInputProps("note")}
        />
        <Group justify="flex-end" gap="xs">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconX size={14} />}
            onClick={onCancel}
          >
            Отмена
          </Button>
          <Button
            size="xs"
            leftSection={<IconCheck size={14} />}
            loading={submitting}
            onClick={handleSubmit}
          >
            Сохранить
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
